import {
  AgeAssuranceAttemptDependencies,
  AgeAssuranceMode,
  PlatformAgeAssuranceResult,
  UserGeo,
} from '../types';

export { AgeAssuranceMode } from '../types';

const supportedModes = new Set<string>(Object.values(AgeAssuranceMode));
const productionModes = new Set<string>([AgeAssuranceMode.OFF, AgeAssuranceMode.LIVE]);

export const resolveAgeAssuranceMode = (value: unknown, production: boolean): AgeAssuranceMode => {
  if (typeof value !== 'string' || !supportedModes.has(value)) {
    return AgeAssuranceMode.OFF;
  }

  if (production && !productionModes.has(value)) {
    return AgeAssuranceMode.OFF;
  }

  return value as AgeAssuranceMode;
};

const resolved = <T>(value: T): Promise<T> => Promise.resolve(value);

export const selectAgeAssuranceDependencies = (
  mode: AgeAssuranceMode,
  liveDependencies: AgeAssuranceAttemptDependencies
): AgeAssuranceAttemptDependencies => {
  const syntheticDependencies = ({
    geo,
    platformResult,
  }: {
    geo: UserGeo | null;
    platformResult?: PlatformAgeAssuranceResult;
  }): AgeAssuranceAttemptDependencies => ({
    queryUserGeo: () => resolved(geo),
    requestPlatformAgeAssurance: () =>
      resolved(
        platformResult ?? {
          outcome: 'native_unavailable',
          nativeResult: 'response_missing',
        }
      ),
    logTerminalDecision: liveDependencies.logTerminalDecision,
  });

  switch (mode) {
    case AgeAssuranceMode.LIVE:
      return liveDependencies;
    case AgeAssuranceMode.QA_TEXAS_LIVE:
      return {
        ...liveDependencies,
        queryUserGeo: () => resolved({ country: 'US', state: 'TX' }),
      };
    case AgeAssuranceMode.QA_OUTSIDE_TEXAS:
      return syntheticDependencies({ geo: { country: 'US', state: 'FL' } });
    case AgeAssuranceMode.QA_UNDER_13:
      return syntheticDependencies({
        geo: { country: 'US', state: 'TX' },
        platformResult: { outcome: 'age_range', lowerBound: 0, upperBound: 12 },
      });
    case AgeAssuranceMode.QA_13_PLUS:
      return syntheticDependencies({
        geo: { country: 'US', state: 'TX' },
        platformResult: { outcome: 'age_range', lowerBound: 13, upperBound: 17 },
      });
    case AgeAssuranceMode.QA_UNAVAILABLE:
    case AgeAssuranceMode.OFF:
      return syntheticDependencies({ geo: null });
  }
};
